import { Link } from "react-router-dom";
import { CloudRain, BatteryCharging, ParkingSquare, Check } from "lucide-react";

const ACTIVE_IMG =
  "https://customer-assets.emergentagent.com/job_mobility-preview-1/artifacts/naj7sz4w_WhatsApp%20Image%202026-03-10%20at%2018.50.50.jpeg";

const BULLETS = [
  {
    icon: CloudRain,
    title: "Guidi sempre all'asciutto",
    desc: "Il comfort di un'auto, l'agilità di uno scooter.",
  },
  {
    icon: BatteryCharging,
    title: "Ricarica facile e bassi consumi",
    desc: "Spina di casa, costi al km imbattibili.",
  },
  {
    icon: ParkingSquare,
    title: "Parcheggi ovunque",
    desc: "Dimentica lo stress di cercare posto in città.",
  },
];

export default function ExperienceActive() {
  // Build pre-filled WhatsApp message for test drive request
  const waMessage = encodeURIComponent(
    "Salve! Sono interessato a prenotare un TEST DRIVE GRATUITO dello scooter cabinato Active. Potete indicarmi le disponibilità?"
  );
  const waHref = `https://wa.me/393297452439?text=${waMessage}`;

  return (
    <section
      id="experience-active"
      data-testid="experience-active-section"
      className="relative bg-white py-20 md:py-28 overflow-hidden"
    >
      {/* Soft background accent */}
      <div
        className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(circle, rgba(90,181,0,0.08) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image — left */}
          <div className="relative order-1 lg:order-1">
            <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.14)] group">
              <img
                src={ACTIVE_IMG}
                alt="Scooter cabinato Active - guida senza patente Zibido San Giacomo Milano"
                className="w-full aspect-[4/5] md:aspect-[5/6] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
              {/* Floating brand badge */}
              <div className="absolute top-5 left-5 glass-panel rounded-full px-5 py-2 flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#7a7f77] font-bold">
                  Active
                </span>
                <span className="w-1 h-1 rounded-full bg-[#5ab500]" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#1c1f1a] font-bold">
                  Senza patente
                </span>
              </div>
              {/* Bottom price/info card */}
              <div className="absolute bottom-5 left-5 right-5 md:right-auto md:max-w-sm glass-panel rounded-2xl p-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#5ab500] font-bold mb-1">
                  Lo scooter cabinato
                </div>
                <div className="font-heading text-lg md:text-xl font-semibold text-[#1c1f1a] leading-tight">
                  Comodo come un'auto, agile come uno scooter.
                </div>
              </div>
            </div>
            {/* Decorative corner */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#5ab500]/40 rounded-br-3xl pointer-events-none hidden md:block" />
          </div>

          {/* Text — right */}
          <div className="order-2 lg:order-2">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#5ab500] font-bold mb-4">
              — L'Esperienza Active
            </div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-[3.4rem] font-light tracking-tight text-[#1c1f1a] leading-[1.05]">
              Active:{" "}
              <span className="italic text-[#5ab500]">Zero pioggia.</span>
              <br />
              Zero traffico.
              <br />
              <span className="font-medium">100% Libertà.</span>
            </h2>

            <p className="mt-6 text-[#4a4f47] leading-relaxed max-w-lg">
              Scopri lo scooter cabinato che sta cambiando il modo di muoversi
              nelle città italiane. Niente casco, niente pioggia, niente
              traffico.
            </p>

            <ul className="mt-8 space-y-4">
              {BULLETS.map(({ icon: Icon, title, desc }) => (
                <li
                  key={title}
                  data-testid={`active-bullet-${title.split(" ")[0].toLowerCase()}`}
                  className="flex items-start gap-4 p-4 rounded-2xl bg-[#f5f6f4] hover:bg-[#5ab500]/5 transition-colors border border-black/[0.04]"
                >
                  <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Icon className="text-[#5ab500]" size={20} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[#1c1f1a] font-bold">
                      <Check size={14} className="text-[#5ab500] flex-shrink-0" />
                      <span>{title}</span>
                    </div>
                    <div className="text-sm text-[#7a7f77] mt-1 leading-relaxed">
                      {desc}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="active-cta-test-drive"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#5ab500] text-white font-bold hover:bg-[#4a9a00] hover:shadow-[0_10px_30px_rgba(90,181,0,0.45)] transition-all"
              >
                Prenota un Test Drive Gratuito
              </a>
              <Link
                to="/veicoli?type=scooter_cabinato#vetrina"
                data-testid="active-cta-vedi-scooter"
                className="inline-flex items-center px-7 py-3.5 rounded-full border border-[#1c1f1a]/20 text-[#1c1f1a] font-semibold hover:border-[#1c1f1a] hover:bg-[#1c1f1a] hover:text-white transition-all"
              >
                Vedi gli scooter cabinati
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
